const LIMIT_RETRY_COUNT = 3;

export interface ApiClientResponse<T> {
  status: number;
  data: T;
}

export const unknownErrorResponse = {
  status: -500,
  errors: ['unexpected error.'],
};

/**
 * ポーリング用のタイマー設定
 */
export function waitPollingTime(ms: number): Promise<void> {
  return new Promise<void>((resolve) => setTimeout(resolve, ms));
}

/**
 * query: クエリパラメータ
 * body: リクエストボディ
 */
type ApiClientConfig = {
  query?: any;
  body?: any;
};

function appendQueryString(url: string, params: Record<string, unknown>): string {
  if (!params || Object.keys(params).length === 0) return url;
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    appendQueryParamValue(search, k, v);
  }
  const qs = search.toString();
  if (!qs) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}${qs}`;
}

function appendQueryParamValue(search: URLSearchParams, key: string, value: unknown): void {
  if (value === undefined || value === null) return;

  if (Array.isArray(value)) {
    for (const item of value) {
      appendQueryParamValue(search, `${key}[]`, item);
    }
    return;
  }

  if (value instanceof Date) {
    search.append(key, value.toISOString());
    return;
  }

  if (typeof value === 'object') {
    search.append(key, JSON.stringify(value));
    return;
  }

  search.append(key, String(value));
}

function isIdempotentMethod(method?: string): boolean {
  return ['GET', 'HEAD', 'OPTIONS', 'PUT', 'DELETE'].includes((method ?? 'GET').toUpperCase());
}

async function parseResponseBody(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

export class ApiClient {
  /**
   * axios-retry 相当: ネットワークエラーおよび冪等メソッドの 5xx のとき最大 LIMIT_RETRY_COUNT 回まで再試行
   */
  private async fetchWithRetry(url: string, init: RequestInit): Promise<Response> {
    for (let attempt = 0; attempt <= LIMIT_RETRY_COUNT; attempt++) {
      try {
        const response = await fetch(url, init);
        if (response.status < 500 || !isIdempotentMethod(init.method)) {
          return response;
        }
        if (attempt < LIMIT_RETRY_COUNT) {
          if (response.body) {
            await response.body.cancel().catch(() => undefined);
          }
          continue;
        }
        return response;
      } catch (_e) {
        if (attempt < LIMIT_RETRY_COUNT) {
          continue;
        }
        throw _e;
      }
    }
    throw new Error('ApiClient.fetchWithRetry: exceeded retries without response');
  }

  /**
   * リクエストヘッダー取得
   */
  private async getRequestHeaders(): Promise<Record<string, string>> {
    return {};
  }

  /**
   * GET
   */
  public async get<T>(path: string, params = {}): Promise<ApiClientResponse<T>> {
    const headers = await this.getRequestHeaders();
    const url = appendQueryString(path, params as Record<string, unknown>);
    return this.request<T>(url, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        ...headers,
      },
    });
  }

  /**
   * POST
   *
   * @param path APIのパス（APIバージョンなどは不要）
   * @param config queryとbodyを指定
   */
  public async post<T>(path: string, config?: ApiClientConfig): Promise<ApiClientResponse<T>> {
    const headers = await this.getRequestHeaders();
    const url = config?.query ? appendQueryString(path, config.query as Record<string, unknown>) : path;
    const hasBody = config?.body !== undefined && config?.body !== null;
    return this.request<T>(url, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
        ...headers,
      },
      body: hasBody ? JSON.stringify(config.body) : undefined,
    });
  }

  /**
   * PUT
   *
   * @param path APIのパス（APIバージョンなどは不要）
   * @param config queryとbodyを指定
   */
  public async put<T>(path: string, config?: ApiClientConfig): Promise<ApiClientResponse<T>> {
    const headers = await this.getRequestHeaders();
    const url = config?.query ? appendQueryString(path, config.query as Record<string, unknown>) : path;
    const hasBody = config?.body !== undefined && config?.body !== null;
    return this.request<T>(url, {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
        ...headers,
      },
      body: hasBody ? JSON.stringify(config.body) : undefined,
    });
  }

  /**
   * DELETE
   */
  public async delete<T>(path: string, config?: ApiClientConfig): Promise<ApiClientResponse<T>> {
    const headers = await this.getRequestHeaders();
    const url = config?.query ? appendQueryString(path, config.query as Record<string, unknown>) : path;
    const hasBody = config?.body !== undefined && config?.body !== null;
    return this.request<T>(url, {
      method: 'DELETE',
      headers: {
        Accept: 'application/json',
        ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
        ...headers,
      },
      body: hasBody ? JSON.stringify(config.body) : undefined,
    });
  }

  /**
   * リクエスト/レスポンスの共通処理
   */
  private async request<T>(url: string, init: RequestInit): Promise<ApiClientResponse<T>> {
    const onSuccess = (status: number, data: T): ApiClientResponse<T> => ({
      status,
      data,
    });

    try {
      const response = await this.fetchWithRetry(url, init);

      if (response.ok) {
        const data = (await parseResponseBody(response)) as T;
        return onSuccess(response.status, data);
      }

      const errorData = (await parseResponseBody(response)) as {
        result?: { errors?: string[] };
      } | null;
      const status = response.status;

      if (![400, 401, 402, 403].includes(status)) {
        return Promise.reject(unknownErrorResponse);
      }
      return Promise.reject({
        status,
        errors: errorData?.result?.errors ?? [],
      });
    } catch (_e) {
      return Promise.reject(unknownErrorResponse);
    }
  }
}

export const apiClient = new ApiClient();
